import { useCallback, useEffect, useState, type FormEvent } from "react";
import { uuidv7 } from "../../../core/platform/id";
import { parseCardInput, formatCode } from "../domain/code";
import { loyaltyTheme, formatPesos } from "../../../theme/loyalty";

/**
 * The loyalty back office (specs/006-loyalty US5, US6): find a card, add or
 * remove stickers with a reason, block or unblock it, say which items are
 * drinks, and see what the programme gave. Needs the connection, as the books
 * do; earning and redeeming at the till never come through here.
 */

interface LoyaltyAdminProps {
  token: string;
  onClose(): void;
}

type Tab = "card" | "items" | "summary";

async function request<T>(token: string, method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api/loyalty${path}`, {
    method,
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(problem?.error?.message ?? `Request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

export function LoyaltyAdmin({ token, onClose }: LoyaltyAdminProps) {
  const [tab, setTab] = useState<Tab>("card");
  return (
    <div className="loy__overlay" role="dialog" aria-label="Loyalty cards">
      <div className="loy__sheet loy__sheet--wide">
        <header className="loy__head">
          <h2>Loyalty cards</h2>
          <button type="button" className="loy__close" onClick={onClose}>
            Close
          </button>
        </header>
        <nav className="loy__tabs">
          {(["card", "items", "summary"] as const).map((name) => (
            <button
              key={name}
              type="button"
              className={`loy__tab${tab === name ? " is-active" : ""}`}
              onClick={() => setTab(name)}
            >
              {name === "card" ? "Find a card" : name === "items" ? "What earns" : "Summary"}
            </button>
          ))}
        </nav>
        {tab === "card" && <CardTab token={token} />}
        {tab === "items" && <ItemsTab token={token} />}
        {tab === "summary" && <SummaryTab token={token} />}
      </div>
    </div>
  );
}

interface CardDetail {
  code: string;
  status: string;
  banked: number;
  onCard: number;
  owed: number;
  lifetimeStickers: number;
  rewardsRedeemed: number;
  updatedAt: string | null;
  stickers: Array<{ id: string; source: string; stickers: number; credited: number; reason: string | null; created_at: string }>;
  blocks: Array<{ id: string; action: string; reason: string; created_at: string }>;
}

function CardTab({ token }: { token: string }) {
  const [input, setInput] = useState("");
  const [card, setCard] = useState<CardDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (code: string) => {
      setError(null);
      try {
        setCard(await request<CardDetail>(token, "GET", `/cards/${code}`));
      } catch (caught) {
        setCard(null);
        setError(caught instanceof Error ? caught.message : "Could not load the card");
      }
    },
    [token],
  );

  const find = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseCardInput(input);
    if (!parsed.ok) {
      setError(parsed.reason === "check" ? loyaltyTheme.till.badCheck : loyaltyTheme.till.badLength);
      return;
    }
    void load(parsed.code);
  };

  const act = async (path: string, body: Record<string, unknown>) => {
    if (!card) return;
    if (reason.trim() === "") {
      setError("Say why — it is kept with the change.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await request(token, "POST", `/cards/${card.code}${path}`, { opId: uuidv7(), reason: reason.trim(), ...body });
      setDelta("");
      setReason("");
      await load(card.code);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const stickers = Number.parseInt(delta, 10);

  return (
    <section className="loy__panel">
      <form className="loy__type" onSubmit={find}>
        <label>
          <span>Card code</span>
          <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="K7M2-QX9P-AB3" autoCapitalize="characters" />
        </label>
        <button type="submit" className="loy__primary">
          Find
        </button>
      </form>

      {error && <p className="loy__error">{error}</p>}

      {card && (
        <>
          <dl className="loy__facts">
            <div>
              <dt>Card</dt>
              <dd className="money">{formatCode(card.code)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{card.status}</dd>
            </div>
            <div>
              <dt>On card</dt>
              <dd>
                {card.onCard} of {loyaltyTheme.rewardEvery}
              </dd>
            </div>
            <div>
              <dt>Rewards ready</dt>
              <dd>{card.banked}</dd>
            </div>
            <div>
              <dt>Lifetime</dt>
              <dd>
                {card.lifetimeStickers} stickers · {card.rewardsRedeemed} redeemed
              </dd>
            </div>
          </dl>

          <div className="loy__adjust">
            <label>
              <span>Stickers (+ or −)</span>
              <input value={delta} onChange={(event) => setDelta(event.target.value)} inputMode="numeric" placeholder="e.g. 2 or -2" />
            </label>
            <label className="loy__grow">
              <span>Why</span>
              <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="e.g. Forgot to scan, 2 Nov" />
            </label>
          </div>
          <div className="loy__actions">
            <button
              type="button"
              className="loy__primary"
              disabled={busy || !Number.isInteger(stickers) || stickers === 0}
              onClick={() => void act("/adjustments", { stickers })}
            >
              Adjust stickers
            </button>
            <button
              type="button"
              className="loy__secondary"
              disabled={busy}
              onClick={() => void act("/blocks", { action: card.status === "blocked" ? "unblock" : "block" })}
            >
              {card.status === "blocked" ? "Unblock card" : "Block card"}
            </button>
          </div>

          <ul className="loy__history">
            {card.stickers.map((row) => (
              <li key={row.id}>
                <span>{new Date(row.created_at).toLocaleString()}</span>
                <span>{row.source}</span>
                <span className="money">{row.stickers > 0 ? `+${row.stickers}` : row.stickers}</span>
                <span>{row.credited ? row.reason ?? "" : "not credited"}</span>
              </li>
            ))}
            {card.blocks.map((row) => (
              <li key={row.id}>
                <span>{new Date(row.created_at).toLocaleString()}</span>
                <span>{row.action}</span>
                <span />
                <span>{row.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

interface ItemRow {
  menuItemId: string;
  name: string;
  kind: "drink" | "pastry" | "none";
  explicit: boolean;
}

function ItemsTab({ token }: { token: string }) {
  const [items, setItems] = useState<ItemRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems((await request<{ items: ItemRow[] }>(token, "GET", "/items")).items);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the menu");
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = async (menuItemId: string, kind: ItemRow["kind"]) => {
    try {
      await request(token, "PUT", `/items/${menuItemId}`, { kind });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save");
    }
  };

  return (
    <section className="loy__panel">
      <p className="loy__note">
        Drinks earn a sticker and can be a reward; pastries can be a reward; "neither" does neither. Anything sold in
        ounces counts as a drink unless you say otherwise.
      </p>
      {error && <p className="loy__error">{error}</p>}
      <ul className="loy__items">
        {(items ?? []).map((item) => (
          <li key={item.menuItemId}>
            <span>{item.name}</span>
            <select value={item.kind} onChange={(event) => void set(item.menuItemId, event.target.value as ItemRow["kind"])}>
              <option value="drink">Drink</option>
              <option value="pastry">Pastry</option>
              <option value="none">Neither</option>
            </select>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface Summary {
  stickersGiven: number;
  rewardsEarned: number;
  rewardsRedeemed: number;
  rewardValue: number;
  rewardsBanked: number;
  distinctCards: number;
}

function monthStart(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function SummaryTab({ token }: { token: string }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void request<Summary>(token, "GET", `/summary?from=${from}&to=${to}`)
      .then((body) => {
        if (!cancelled) setSummary(body);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "Could not load the summary");
      });
    return () => {
      cancelled = true;
    };
  }, [token, from, to]);

  return (
    <section className="loy__panel">
      <div className="loy__adjust">
        <label>
          <span>From</span>
          <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          <span>To</span>
          <input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>
      {error && <p className="loy__error">{error}</p>}
      {summary && (
        <dl className="loy__facts">
          <div>
            <dt>Stickers given</dt>
            <dd>{summary.stickersGiven}</dd>
          </div>
          <div>
            <dt>Rewards earned</dt>
            <dd>{summary.rewardsEarned}</dd>
          </div>
          <div>
            <dt>Rewards redeemed</dt>
            <dd>
              {summary.rewardsRedeemed} · {formatPesos(summary.rewardValue)}
            </dd>
          </div>
          <div>
            <dt>Rewards waiting on cards</dt>
            <dd>{summary.rewardsBanked}</dd>
          </div>
          <div>
            <dt>Cards used</dt>
            <dd>{summary.distinctCards}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
